/*
 * Portions of this code are from Novel (https://github.com/steven-tey/novel)
 * Copyright (c) 2023 Steven Tey
 * Licensed under the Apache License, Version 2.0
 */

import { useEditor } from '@/components/tiptap-editor'
import { cn } from '@/utils/cn'
import { Button, PopoverContent } from '@blogging/ui'
import { Popover, PopoverTrigger } from '@radix-ui/react-popover'
import { Bookmark, Check, ExternalLink, Trash } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export function isValidUrl(url: string) {
  try {
    new URL(url)
    return true
  } catch (_e) {
    return false
  }
}
export function getUrlFromString(str: string) {
  if (isValidUrl(str)) return str
  try {
    if (str.includes('.') && !str.includes(' ')) {
      return new URL(`https://${str}`).toString()
    }
  } catch (_e) {
    return null
  }
}
interface LinkSelectorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const LinkSelector = ({ open, onOpenChange }: LinkSelectorProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const { editor } = useEditor()

  // Get current link attributes
  const currentHref = editor?.getAttributes('link').href || ''
  const currentTarget = editor?.getAttributes('link').target

  // State for open in new window toggle
  const [openInNewWindow, setOpenInNewWindow] = useState(
    currentTarget === '_blank',
  )

  // Update state when popover opens with existing link
  useEffect(() => {
    if (open && editor) {
      const target = editor.getAttributes('link').target
      setOpenInNewWindow(target === '_blank')
    }
  }, [open, editor])

  // Autofocus on input by default
  useEffect(() => {
    if (open && inputRef && inputRef.current) {
      inputRef.current.focus()
    }
  }, [open])

  if (!editor) return null

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const target = e.currentTarget as HTMLFormElement
    const input = target[0] as HTMLInputElement
    const url = getUrlFromString(input.value)
    if (url) {
      editor
        .chain()
        .focus()
        .setLink({
          href: url,
          target: openInNewWindow ? '_blank' : null,
        })
        .run()
      onOpenChange(false)
    }
  }

  const handleToggleNewWindow = () => {
    const newValue = !openInNewWindow
    setOpenInNewWindow(newValue)

    // If there's already a link, update it immediately
    if (currentHref) {
      editor
        .chain()
        .focus()
        .setLink({
          href: currentHref,
          target: newValue ? '_blank' : null,
        })
        .run()
    }
  }

  return (
    <Popover modal={true} open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="gap-2 rounded-none border-none"
        >
          <p className="text-base">↗</p>
          <p
            className={cn('underline decoration-stone-400 underline-offset-4', {
              'text-blue-500': editor.isActive('link'),
            })}
          >
            Link
          </p>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" sideOffset={10}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-2 p-2">
          <div className="flex gap-1">
            <input
              ref={inputRef}
              type="text"
              placeholder="Paste a link"
              className="flex-1 bg-background p-1 text-sm outline-none border rounded px-2"
              defaultValue={currentHref}
            />
            {currentHref ? (
              <div className="flex gap-1">
                <Button
                  size="icon"
                  variant="outline"
                  type="button"
                  className="flex h-8 items-center rounded-sm p-1"
                  title="Convert to link card"
                  onClick={() => {
                    editor.chain().focus().convertLinkToCard().run()
                    if (inputRef && inputRef.current) {
                      inputRef.current.value = ''
                    }
                    onOpenChange(false)
                  }}
                >
                  <Bookmark className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  type="button"
                  className="flex h-8 items-center rounded-sm p-1 text-red-600 transition-all hover:bg-red-100 dark:hover:bg-red-800"
                  onClick={() => {
                    editor.chain().focus().unsetLink().run()
                    if (inputRef && inputRef.current) {
                      inputRef.current.value = ''
                    }
                    setOpenInNewWindow(false)
                    onOpenChange(false)
                  }}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button size="icon" className="h-8">
                <Check className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Open in new window toggle */}
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors py-1">
            <input
              type="checkbox"
              checked={openInNewWindow}
              onChange={handleToggleNewWindow}
              className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
            />
            <ExternalLink className="h-3.5 w-3.5" />
            <span>Open in new window</span>
          </label>
        </form>
      </PopoverContent>
    </Popover>
  )
}
